import React, { useState } from 'react';
import { 
  Container, 
  Grid, 
  Typography, 
  Box, 
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  useTheme,
  alpha,
  LinearProgress
} from '@mui/material';
import { 
  PictureAsPdf, 
  GetApp, 
  Assessment, 
  DateRange,
  TrendingUp,
  Inventory,
  People,
  ShoppingCart,
  CheckCircle,
  Schedule
} from '@mui/icons-material';
import PremiumCard from '../components/ui/PremiumCard';
import PageHeader from '../components/ui/PageHeader';
import { DESIGN_SYSTEM } from '../theme/designSystem';

/**
 * Página de Reportes Exportables
 * Permite generar y descargar reportes en PDF
 */
export default function ReportsNew() {
  const theme = useTheme();
  const [selectedReport, setSelectedReport] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [generating, setGenerating] = useState(false);

  const reportTypes = [
    {
      id: 'sales',
      name: 'Reporte de Ventas',
      description: 'Análisis completo de ventas, productos top y tendencias',
      icon: <TrendingUp />,
      color: theme.palette.primary.main,
      pages: '8-12 páginas',
      includes: ['Ventas por período', 'Productos más vendidos', 'Análisis de tendencias', 'Métricas clave']
    },
    {
      id: 'products',
      name: 'Reporte de Productos',
      description: 'Rendimiento detallado de productos y categorías',
      icon: <Inventory />,
      color: theme.palette.success.main,
      pages: '6-10 páginas',
      includes: ['Rendimiento por producto', 'Análisis de categorías', 'Stock y rotación', 'Recomendaciones']
    },
    {
      id: 'customers',
      name: 'Reporte de Clientes',
      description: 'Segmentación y comportamiento de clientes',
      icon: <People />,
      color: theme.palette.info.main,
      pages: '5-8 páginas',
      includes: ['Segmentación RFM', 'Clientes VIP', 'Retención', 'Análisis demográfico']
    },
    {
      id: 'complete',
      name: 'Reporte Ejecutivo Completo',
      description: 'Análisis integral de toda la tienda',
      icon: <Assessment />,
      color: theme.palette.secondary.main,
      pages: '15-20 páginas',
      includes: ['Todas las métricas', 'Análisis ejecutivo', 'Recomendaciones estratégicas', 'Proyecciones'],
      premium: true
    }
  ];

  const recentReports = [
    {
      name: 'Reporte de Ventas - Julio 2025',
      date: '15 Jul 2025',
      type: 'sales',
      status: 'completed',
      size: '2.3 MB'
    },
    {
      name: 'Reporte Ejecutivo - Q2 2025',
      date: '30 Jun 2025',
      type: 'complete',
      status: 'completed',
      size: '4.7 MB'
    },
    {
      name: 'Análisis de Productos - Junio 2025',
      date: '28 Jun 2025',
      type: 'products',
      status: 'completed',
      size: '1.8 MB'
    }
  ];

  const handleGenerateReport = async () => {
    if (!selectedReport) return;
    
    setGenerating(true);
    
    // Simular generación de reporte
    setTimeout(() => {
      setGenerating(false);
      // Aquí iría la lógica real de generación y descarga
      alert('Reporte generado exitosamente. La descarga comenzará automáticamente.');
    }, 3000);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Unificado */}
      <PageHeader 
        title={DESIGN_SYSTEM.pageHeaders.reports.title}
        subtitle={DESIGN_SYSTEM.pageHeaders.reports.subtitle}
        icon={<Assessment />}
      />

      <Grid container spacing={3}>
        {/* Generador de Reportes */}
        <Grid item xs={12} md={8}>
          <PremiumCard
            title="Generar Nuevo Reporte"
            subtitle="Crea reportes personalizados en PDF"
            icon={<PictureAsPdf />}
            badge="Premium"
          >
            <Box mb={3}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Tipo de Reporte</InputLabel>
                <Select
                  value={selectedReport}
                  onChange={(e) => setSelectedReport(e.target.value)}
                  label="Tipo de Reporte"
                >
                  {reportTypes.map((report) => (
                    <MenuItem key={report.id} value={report.id}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {report.icon}
                        <Box>
                          <Typography variant="body1">{report.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {report.pages}
                          </Typography>
                        </Box>
                        {report.premium && (
                          <Chip label="Premium" size="small" color="primary" />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Período</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Período"
                >
                  <MenuItem value="7">Últimos 7 días</MenuItem>
                  <MenuItem value="30">Últimos 30 días</MenuItem>
                  <MenuItem value="90">Últimos 3 meses</MenuItem>
                  <MenuItem value="365">Último año</MenuItem>
                  <MenuItem value="custom">Período personalizado</MenuItem>
                </Select>
              </FormControl>

              {dateRange === 'custom' && (
                <Box display="flex" gap={2} mb={2}>
                  <TextField
                    label="Fecha inicio"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="Fecha fin"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              )}
            </Box>

            {selectedReport && (
              <Card sx={{ mb: 3, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    {reportTypes.find(r => r.id === selectedReport)?.icon}
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {reportTypes.find(r => r.id === selectedReport)?.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {reportTypes.find(r => r.id === selectedReport)?.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="subtitle2" gutterBottom>
                    Este reporte incluye:
                  </Typography>
                  <List dense>
                    {reportTypes.find(r => r.id === selectedReport)?.includes.map((item, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}

            {generating && (
              <Box mb={3}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Generando reporte...
                </Typography>
                <LinearProgress />
              </Box>
            )}

            <Button
              variant="contained"
              size="large"
              startIcon={<GetApp />}
              onClick={handleGenerateReport}
              disabled={!selectedReport || generating}
              fullWidth
            >
              {generating ? 'Generando Reporte...' : 'Generar y Descargar PDF'}
            </Button>
          </PremiumCard>
        </Grid>

        {/* Reportes Recientes */}
        <Grid item xs={12} md={4}>
          <PremiumCard
            title="Reportes Recientes"
            icon={<Schedule />}
          >
            <List>
              {recentReports.map((report, index) => (
                <ListItem key={index} sx={{ px: 0, py: 1 }}>
                  <ListItemIcon>
                    {reportTypes.find(r => r.id === report.type)?.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={report.name}
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {report.date} • {report.size}
                        </Typography>
                        <br />
                        <Chip 
                          label="Completado" 
                          size="small" 
                          color="success"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    }
                  />
                  <Button size="small" startIcon={<GetApp />}>
                    Descargar
                  </Button>
                </ListItem>
              ))}
            </List>
          </PremiumCard>
        </Grid>

        {/* Información sobre Reportes */}
        <Grid item xs={12}>
          <PremiumCard
            title="Sobre los Reportes de Nubelytics"
            icon={<Assessment />}
          >
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box textAlign="center" p={2}>
                  <PictureAsPdf sx={{ fontSize: 48, color: theme.palette.error.main, mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Formato PDF Profesional
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Reportes diseñados profesionalmente, listos para presentar a inversores o stakeholders.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box textAlign="center" p={2}>
                  <TrendingUp sx={{ fontSize: 48, color: theme.palette.success.main, mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Datos en Tiempo Real
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Todos los reportes se generan con los datos más actuales de tu tienda.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box textAlign="center" p={2}>
                  <CheckCircle sx={{ fontSize: 48, color: theme.palette.info.main, mb: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Insights Accionables
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cada reporte incluye recomendaciones específicas para mejorar tu negocio.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </PremiumCard>
        </Grid>
      </Grid>
    </Container>
  );
}
